// museumsDatabase.js - Museum operations via Cloud Function
// Uses Firebase Cloud Function to write to ai-tourguide-1dc92 with admin permissions

import { getAuth } from 'firebase/auth';

const CLOUD_FUNCTION_URL = 'https://us-central1-guidesofia-access-control.cloudfunctions.net/museumObjectsHandler';

// Operations that require authentication
const WRITE_OPERATIONS = [
  'createMuseum', 'updateMuseum', 'deleteMuseum',
  'createObject', 'updateObject', 'deleteObject'
];

/**
 * Helper function to call the Cloud Function
 */
async function callCloudFunction(operation, data = {}) {
  try {
    const headers = {
      'Content-Type': 'application/json'
    };

    // Add auth token for write operations
    if (WRITE_OPERATIONS.includes(operation)) {
      const auth = getAuth();
      const user = auth.currentUser;
      if (user) {
        const token = await user.getIdToken();
        headers['Authorization'] = `Bearer ${token}`;
      } else {
        throw new Error('Authentication required. Please sign in.');
      }
    }

    const response = await fetch(CLOUD_FUNCTION_URL, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        operation,
        ...data
      })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || error.message || `HTTP ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error(`Cloud Function error (${operation}):`, error);
    throw error;
  }
}

// Create a new museum
export const createMuseum = async (museumData) => {
  console.log('DEBUG: Creating museum with data:', museumData);

  const data = {
    name: museumData.name,
    description: museumData.description || '',
    rooms: museumData.rooms || [],
    categories: museumData.categories || [
      'sculpture', 'painting', 'artifact', 'jewelry', 'textile',
      'ceramics', 'furniture', 'manuscript', 'photography', 'other'
    ],
    linkedGuideIds: museumData.linkedGuideIds || [],
    address: museumData.address || '',
    website: museumData.website || '',
    phone: museumData.phone || '',
    createdBy: museumData.createdBy || 'anonymous'
  };

  const result = await callCloudFunction('createMuseum', { data });
  console.log('Museum created with ID:', result.id);
  return result;
};

// Update museum record
export const updateMuseum = async (museumId, updates) => {
  return await callCloudFunction('updateMuseum', {
    museumId,
    data: updates
  });
};

// Get single museum by ID
export const getMuseum = async (museumId) => {
  return await callCloudFunction('getMuseum', { museumId });
};

// Get all museums
export const getAllMuseums = async () => {
  console.log('DEBUG: Fetching all museums');
  return await callCloudFunction('getAllMuseums');
};

// Delete museum
export const deleteMuseum = async (museumId) => {
  return await callCloudFunction('deleteMuseum', { museumId });
};

// Add a room to museum
export const addRoom = async (museumId, room) => {
  const museum = await getMuseum(museumId);
  const rooms = museum.rooms || [];

  const roomWithId = {
    id: room.id || `room-${Date.now()}`,
    name: room.name,
    floor: room.floor || 1
  };

  rooms.push(roomWithId);

  await updateMuseum(museumId, { rooms });

  return { success: true, room: roomWithId };
};

// Remove a room from museum
export const removeRoom = async (museumId, room) => {
  const museum = await getMuseum(museumId);
  const rooms = (museum.rooms || []).filter(r => r.id !== room.id);

  await updateMuseum(museumId, { rooms });

  return { success: true };
};

// Update rooms list (for reordering)
export const updateRooms = async (museumId, rooms) => {
  return await updateMuseum(museumId, { rooms });
};

// Add a category to museum
export const addCategory = async (museumId, category) => {
  const museum = await getMuseum(museumId);
  const categories = museum.categories || [];

  if (!categories.includes(category)) {
    categories.push(category);
    await updateMuseum(museumId, { categories });
  }

  return { success: true };
};

// Remove a category from museum
export const removeCategory = async (museumId, category) => {
  const museum = await getMuseum(museumId);
  const categories = (museum.categories || []).filter(c => c !== category);

  await updateMuseum(museumId, { categories });

  return { success: true };
};

// Update categories list
export const updateCategories = async (museumId, categories) => {
  return await updateMuseum(museumId, { categories });
};

// Link a guide to this museum
export const linkGuide = async (museumId, guideId) => {
  const museum = await getMuseum(museumId);
  const linkedGuideIds = museum.linkedGuideIds || [];

  if (!linkedGuideIds.includes(guideId)) {
    linkedGuideIds.push(guideId);
    await updateMuseum(museumId, { linkedGuideIds });
  }

  return { success: true };
};

// Unlink a guide from this museum
export const unlinkGuide = async (museumId, guideId) => {
  const museum = await getMuseum(museumId);
  const linkedGuideIds = (museum.linkedGuideIds || []).filter(id => id !== guideId);

  await updateMuseum(museumId, { linkedGuideIds });

  return { success: true };
};

// Update all linked guides
export const updateLinkedGuides = async (museumId, guideIds) => {
  return await updateMuseum(museumId, { linkedGuideIds: guideIds });
};

// Get museum by linked guide ID (find which museum a guide belongs to)
export const getMuseumByGuideId = async (guideId) => {
  const museums = await getAllMuseums();

  for (const museum of museums) {
    if (museum.linkedGuideIds && museum.linkedGuideIds.includes(guideId)) {
      return museum;
    }
  }

  return null;
};

const museumsDatabase = {
  createMuseum,
  updateMuseum,
  getMuseum,
  getAllMuseums,
  deleteMuseum,
  addRoom,
  removeRoom,
  updateRooms,
  addCategory,
  removeCategory,
  updateCategories,
  linkGuide,
  unlinkGuide,
  updateLinkedGuides,
  getMuseumByGuideId
};

export default museumsDatabase;
